import { Spinner } from "@heroui/react";
import React from "react";
import { NotificationDto, ProfileDto, ProfileId } from "../../types/types";
import { sanitizeHtml } from "../../utils/safeHtml";
import { formatTimestamp } from "../../utils/utilMethods";

interface NotificationItemProps {
  notification: NotificationDto;
  isUpdating: boolean;
  onToggleRead: () => void;
  /** when given, clicking sender name opens the sender's profile */
  onProfileClick?: (profileId: ProfileId) => void;
}

const getDisplayName = ({ firstName, lastName, email }: ProfileDto): string =>
  [firstName, lastName].filter(Boolean).join(" ") || email || "";

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  isUpdating,
  onToggleRead,
  onProfileClick,
}) => {
  const { from, message, time, viewStatus } = notification;
  const displayName = getDisplayName(from);

  return (
    <div
      className={`cursor-pointer p-4 duration-200 hover:bg-default-50 ${
        viewStatus ? "" : "border-l-4 border-l-primary bg-primary-50"
      }`}
      onClick={onToggleRead}
    >
      <div className="flex items-start justify-between gap-3 sm:gap-4">
        <div className="min-w-0 flex-1">
          <div
            className={`break-words text-left text-sm leading-relaxed ${
              viewStatus ? "text-default-700" : "font-medium text-foreground"
            }`}
          >
            {displayName && (
              <span
                className={`font-bold mr-1 ${
                  onProfileClick && from.id ? "cursor-pointer hover:underline text-gray-900" : ""
                }`}
                onClick={
                  onProfileClick && from.id
                    ? (event) => {
                        event.stopPropagation();
                        if (from.id) onProfileClick(from.id);
                      }
                    : undefined
                }
              >
                {displayName}
              </span>
            )}
            <span
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(message),
              }}
            />
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2 self-start pt-0.5 ml-2">
          <span className="text-xs text-default-400 whitespace-nowrap">
            {formatTimestamp(time)}
          </span>
          {!viewStatus && (
            <div className="h-2 w-2 rounded-full bg-primary shrink-0" />
          )}
          {isUpdating && <Spinner size="sm" className="shrink-0" />}
        </div>
      </div>
    </div>
  );
};

export default NotificationItem;
